<template>
  <div class="saved-filters-dropdown-container">
    <b-dropdown
      v-if="savedFilters.length > 0"
      id="saved-filters-dropdown"
      ref="dropdown"
      variant="primary"
      class="saved-filters-dropdown"
      no-caret
    >
      <template #button-content>
        <div class="d-flex align-items-center small-text">
          <span>Saved Filters</span>
          <feather-icon
            icon="ChevronDownIcon"
            size="14"
            class="ml-1"
          />
        </div>
      </template>

      <div class="dropdown-content">
        <div class="header-section d-flex align-items-center justify-content-between">
          <h6 class="text-uppercase font-weight-bolder mb-0">
            SAVED FILTERS
          </h6>
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="cursor-pointer text-primary"
            :class="{ 'rotate-animation': isFetching }"
            @click="fetchSavedFilters"
          />
        </div>

        <div class="content-section">
          <div
            class="save-current-btn mb-2 text-center"
            @click="$emit('save-current')"
          >
            <span class="text-primary font-weight-bold">+ Save Current Filter</span>
          </div>

          <div class="saved-filters-list mb-2">
            <h6 class="small font-weight-bold text-muted mb-1">
              Saved Filters
            </h6>
            <div class="saved-filters-items-container">
              <div
                v-for="filter in savedFilters"
                :key="filter.id"
                class="d-flex align-items-center justify-content-between saved-filter-item px-1"
                :class="{ 'selected-filter': selectedFilterId === filter.id }"
                @click="selectFilter(filter)"
              >
                <span
                  class="filter-name"
                  :title="filter.name"
                >{{ filter.name }}</span>
                <div class="filter-actions">
                  <feather-icon
                    icon="EditIcon"
                    size="14"
                    class="mr-1 action-icon"
                    @click.stop="$emit('edit-filter', filter)"
                  />
                  <feather-icon
                    icon="XIcon"
                    size="14"
                    class="text-danger action-icon"
                    @click.stop="deleteFilter(filter.id)"
                  />
                </div>
              </div>
              <div
                v-if="savedFilters.length === 0"
                class="text-center small text-muted p-1"
              >
                No saved filters
              </div>
            </div>
          </div>
        </div>

        <div class="footer-section d-flex justify-content-between border-top">
          <b-button
            variant="primary"
            size="sm"
            class="flex-grow-1 mr-1"
            :disabled="!selectedFilterId"
            @click="handleApply"
          >
            Apply
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="flex-grow-1"
            @click="closeDropdown"
          >
            Cancel
          </b-button>
        </div>
      </div>
    </b-dropdown>
    <b-button
      v-else
      variant="primary"
      @click="$emit('save-current')"
    >
      Save Filter
    </b-button>
  </div>
</template>

<script>
import {
  BDropdown, BButton,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ErrorMessage from '@core/mixins/ErrorMessage'
import SuccessMessage from '@core/mixins/SuccessMessage'

const savedFilterRepository = RepositoryFactory.get('savedFilter')

export default {
  name: 'SavedFiltersDropdown',
  components: {
    BDropdown,
    BButton,
    FeatherIcon,
  },
  mixins: [ErrorMessage, SuccessMessage],
  props: {
    screen: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isFetching: false,
      savedFilters: [],
      selectedFilterId: null,
      selectedFilterData: null,
    }
  },
  mounted() {
    this.fetchSavedFilters()
  },
  methods: {
    async fetchSavedFilters() {
      this.isFetching = true
      try {
        const { data } = await savedFilterRepository.list(this.screen)
        // Handle both { data: [...] } and [...] response formats
        let filters = data.data || data
        if (!Array.isArray(filters)) {
          filters = []
        }
        filters.sort((a, b) => {
          const dateA = a.created_at || a.updated_at || null
          const dateB = b.created_at || b.updated_at || null
          if (dateA && dateB) {
            return new Date(dateB) - new Date(dateA)
          }
          const idA = parseInt(a.id, 10)
          const idB = parseInt(b.id, 10)
          if (!Number.isNaN(idA) && !Number.isNaN(idB)) {
            return idB - idA
          }
          return String(b.id || '').localeCompare(String(a.id || ''))
        })
        this.savedFilters = filters
      } catch (error) {
        this.convertAndNotifyError(error)
      } finally {
        this.isFetching = false
      }
    },
    selectFilter(filter) {
      this.selectedFilterId = filter.id
      this.selectedFilterData = filter.filters
    },
    handleApply() {
      if (this.selectedFilterData) {
        this.$emit('apply-filter', this.selectedFilterData)
        this.closeDropdown()
      }
    },
    async deleteFilter(id) {
      const confirm = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this filter?', {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      })
      if (confirm) {
        try {
          await savedFilterRepository.delete(id)
          this.showSuccessMessage('Filter deleted successfully')
          this.fetchSavedFilters()
          if (this.selectedFilterId === id) {
            this.selectedFilterId = null
            this.selectedFilterData = null
          }
        } catch (error) {
          this.convertAndNotifyError(error)
        }
      }
    },
    closeDropdown() {
      if (this.$refs.dropdown) {
        this.$refs.dropdown.hide(true)
      }
    },
  },
}
</script>

<style scoped>
.saved-filters-dropdown-container ::v-deep .dropdown-menu {
  padding: 0;
  border-radius: 8px;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  border: none;
  max-height: none !important;
}
.dropdown-content {
  width: 240px;
  cursor: default;
  background-color: #fff;
}
.header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #ebe9f1;
}
.content-section {
  padding: 16px;
}
.save-current-btn {
  border: 1px dashed #7367f0;
  border-radius: 6px;
  cursor: pointer;
  padding: 10px;
  transition: all 0.2s;
}
.save-current-btn:hover {
  background-color: rgba(115, 103, 240, 0.04);
}
.saved-filters-list {
  margin-bottom: 0.5rem;
}
.saved-filters-items-container {
  max-height: 230px;
  overflow-y: auto;
}
.saved-filters-items-container::-webkit-scrollbar {
  width: 6px;
}
.saved-filters-items-container::-webkit-scrollbar-track {
  background: transparent;
}
.saved-filters-items-container::-webkit-scrollbar-thumb {
  background: #ebe9f1;
  border-radius: 3px;
}
.saved-filters-items-container::-webkit-scrollbar-thumb:hover {
  background: #d8d6de;
}
.saved-filter-item {
  cursor: pointer;
  border-radius: 4px;
  padding: 8px 0;
  transition: all 0.2s;
  margin-bottom: 4px;
}
.saved-filter-item:hover {
  background-color: #f8f8f8;
}
.selected-filter {
  background-color: #f3f2f7;
}
.filter-name {
  font-size: 0.85rem;
  color: #5e5873;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
}
.filter-actions {
  flex-shrink: 0;
}
.action-icon {
  cursor: pointer;
  opacity: 0.7;
}
.action-icon:hover {
  opacity: 1;
}
.footer-section {
  padding: 12px 16px;
}
.small-text {
  font-size: 0.85rem;
}
.rotate-animation {
  animation: rotate 1s linear infinite;
}
@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
